/** Namespace `googleCalendar` (Settings + Event-Modal) — befüllt während der Migration. */
export const googleCalendar = {
  de: {
    googleCalendar: {
      // --- Settings ---
      title: 'Google Calendar',
      description: 'Steuert, mit welchem Service-Account und in welchen Kalender Termine geschrieben werden.',
      loading: 'Lade Google-Calendar-Einstellungen…',
      saving: 'Speichere…',
      testing: 'Teste…',
      testConnection: 'Verbindung testen',
      remove: 'Entfernen',
      connected: 'Verbunden',
      notConnected: 'Nicht verbunden',
      sourceLabel: 'Quelle: {{source}}',
      sourcePersisted: 'Persistiert (UI gespeichert)',
      sourceEnv: 'Aus Umgebungsvariable',
      sourceDefault: 'Default (Service-Account-Kalender)',
      activeServiceAccount: 'Aktiver Service-Account',
      unknown: '(unbekannt)',
      projectLabel: 'Projekt:',
      clientIdLabel: 'Client-ID:',
      replaceServiceAccount: 'Service-Account austauschen',
      replaceServiceAccountHint:
        'JSON-Schlüsseldatei aus der Google Cloud Console hochladen (IAM & Admin → Service Accounts → Keys → „Add key" → JSON). Die vorherige Datei wird automatisch als <code>service-account.json.bak</code> gesichert; bei fehlgeschlagener Authentifizierung wird zurückgerollt.',
      uploadingAuthenticating: 'Lade hoch & authentifiziere…',
      accessWaysHint:
        'Zwei Wege, damit der Account auf einen Kalender zugreifen kann: (1) Kalender direkt für die Adresse oben als <strong>„Änderungen an Terminen vornehmen"</strong> freigeben, oder (2) <strong>Domain-Wide Delegation</strong> einrichten und im Feld <em>„Impersonate als"</em> unten den Workspace-User eintragen, in dessen Namen der Account handeln soll.',
      newMainCalendar: 'Neuer Hauptkalender',
      newMainCalendarSub: '(Standard für neue Termine)',
      activeBadge: 'AKTIV',
      newMainCalendarHint:
        'Neue Termine landen ab Speichern in diesem Kalender. Voraussetzung: der Service-Account <code>{{email}}</code> ist in den Freigabe-Einstellungen mit Rolle „Änderungen an Terminen vornehmen" hinzugefügt. Leer lassen → der bisherige Kalender unten bleibt Default.',
      saveFirstTitle: 'Erst speichern, dann testbar',
      envFallbackAvailable: 'ℹ️ Env-Fallback verfügbar: <code>{{value}}</code>',
      envFallbackAvailableReset: 'ℹ️ Env-Fallback verfügbar: <code>{{value}}</code> (greift nach Reset auf Default).',
      legacyCalendar: 'Bisheriger Kalender',
      legacyCalendarSub: '(Bestandstermine, nur auf Anfrage Default)',
      legacyCalendarHint:
        'Bestehende Termine bleiben in diesem Kalender. Neue Termine landen hier nur, wenn beim Anlegen „Bisheriger Kalender" gewählt wird — oder wenn oben kein Hauptkalender konfiguriert ist. ⚠️ Änderungen hier können bestehende Termin-Syncs brechen.',
      extraCalendars: 'Zusatzkalender',
      extraCalendarsSub: '(nur Anzeige, kein Schreib-Ziel)',
      extraCalendarsHint:
        'Diese Kalender werden im Admin-Kalender zusätzlich angezeigt — Termine werden dort niemals angelegt oder geändert. Voraussetzung: Der Kalender-Besitzer gibt seinen Kalender für <code>{{email}}</code> frei (Google Calendar → Einstellungen → „Für bestimmte Personen freigeben", Berechtigung „Alle Termindetails anzeigen" reicht).',
      extraAdd: 'Hinzufügen',
      extraSaved: 'Zusatzkalender gespeichert.',
      extraAlreadyAdded: 'Dieser Kalender ist bereits eingetragen.',
      pseudonymizedSync: 'Pseudonymisierter Sync',
      pseudonymizedSyncSub: '(keine Patientendaten an Google)',
      pseudonymizedSyncHint:
        'Wenn aktiv, enthalten Google-Events nur Behandlungsart und Termin-Nummer – Name, Kontaktdaten und Notizen der Patienten bleiben auf dem Server. Gilt für Termine, die ab jetzt angelegt oder geändert werden; bestehende Events behalten ihren Inhalt bis zur nächsten Änderung.',
      pseudonymizedOn: 'Aktiv – Events ohne Patientendaten',
      pseudonymizedOff: 'Inaktiv – Events mit Patientenname & Kontaktdaten',
      pseudonymizedSaved: 'Einstellung für pseudonymisierten Sync gespeichert.',
      impersonateAs: 'Impersonate als',
      impersonateAsSub: '(optional, Domain-Wide Delegation)',
      impersonatePlaceholder: 'leer lassen für Standard — oder z.B. smile@seadentpalma.com',
      impersonateHint:
        'Wenn deine Google Workspace-Org externes Calendar-Sharing blockt, hier den Workspace-Nutzer eintragen, in dessen Namen der Service-Account handeln soll. Voraussetzung: In <code>admin.google.com → Sicherheit → API-Steuerung → Domainweite Delegierung</code> ist die Client-ID <code>{{clientId}}</code> mit den Scopes <code>auth/calendar</code> und <code>auth/calendar.events</code> autorisiert.',
      impersonateActive: '✓ Aktiv impersoniert: <code>{{value}}</code>',
      resetNotNeededTitle: 'Aktuell nicht persistiert — kein Reset nötig',
      resetTitle: 'Auf env/Default zurückfallen',
      resetting: 'Setze zurück…',
      resetToDefault: 'Auf Default zurücksetzen',
      serviceNotInitTitle: 'Service nicht initialisiert',
      saveLegacyTitle: 'Speichert nur den bisherigen Kalender + Impersonation. Der neue Hauptkalender hat oben einen eigenen Speichern-Button.',
      saveLegacy: 'Bisherigen speichern',
      // Settings-Toasts / Fehler
      errorLoad: 'Konnte Google-Calendar-Einstellungen nicht laden.',
      errorCalendarIdEmpty: 'Calendar-ID darf nicht leer sein.',
      settingsSaved: 'Einstellungen gespeichert.',
      errorSave: 'Speichern fehlgeschlagen.',
      accessOk: '✅ Zugriff OK — "{{summary}}" ({{timeZone}})',
      errorTest: 'Test fehlgeschlagen.',
      secondarySaved: '✅ Neuer Hauptkalender gespeichert: {{id}}',
      secondaryRemoved: 'Neuer Hauptkalender entfernt.',
      errorNotJson: 'Bitte eine .json-Datei wählen (Google Service-Account Key).',
      errorFileTooLarge: 'Datei zu groß — Service-Account-JSONs sind üblicherweise unter 5 KB.',
      confirmReplaceSA:
        'Service-Account ersetzen mit "{{name}}"?\n\nDie aktuelle Datei wird als service-account.json.bak gesichert. Schlägt die Authentifizierung mit dem neuen Account fehl, wird automatisch zurückgerollt.',
      saActive: '✅ Service-Account aktiv: {{email}}',
      errorUpload: 'Upload fehlgeschlagen.',
      confirmReset: 'Persistierte Calendar-ID löschen und auf Default (env/Service-Account) zurückfallen?',
      resetDone: 'Auf Default zurückgesetzt.',
      errorReset: 'Reset fehlgeschlagen.',
      // --- Sync-Status (automatischer Health-Check pro Kalender) ---
      syncStatusTitle: 'Sync-Status',
      syncStatusSub: '(beim Öffnen automatisch geprüft)',
      syncRecheck: 'Neu prüfen',
      syncRechecking: 'Prüfe…',
      syncChecking: 'Prüfe…',
      syncOk: 'Sync OK',
      syncOkRead: 'Lesen OK',
      syncOkWrite: 'Lesen + Schreiben OK',
      syncFailed: 'Fehler',
      syncRoleMain: 'Hauptkalender (Schreiben)',
      syncRoleLegacy: 'Bisheriger Kalender',
      syncRoleExtra: 'Zusatzkalender (nur Anzeige)',
      syncNoCalendars: 'Keine Kalender konfiguriert.',
      syncServiceDown: 'Service nicht initialisiert — kein Sync möglich. Service-Account prüfen.',
      syncDetailOk: '„{{summary}}" · {{timeZone}}',
      // --- Auto-Import eingebundener Kalender ---
      autoImport: {
        title: 'Auto-Import',
        subtitle: 'Übernimmt neue Events der eingebundenen Kalender automatisch als App-Termine.',
        enable: 'Auto-Import aktivieren',
        windowBack: 'Tage rückwärts',
        windowAhead: 'Tage vorwärts',
        perCalendarHint: 'Pro Kalender festlegen, ob importiert wird und mit welchen Standardwerten neue Termine angelegt werden. Behandler, Behandlung und Raum sind Pflicht, damit ein Kalender importiert werden kann.',
        mainCalendarLabel: 'Hauptkalender',
        defaultProvider: 'Behandler',
        defaultProcedure: 'Behandlung',
        defaultOperatory: 'Raum',
        providerPlaceholder: 'Behandler wählen…',
        procedurePlaceholder: 'Behandlung wählen…',
        operatoryPlaceholder: 'Raum wählen…',
        enableCalendar: 'Importieren',
        save: 'Auto-Import speichern',
        saving: 'Speichere…',
        saved: 'Auto-Import-Einstellungen gespeichert.',
        errorSave: 'Speichern der Auto-Import-Einstellungen fehlgeschlagen.',
        errorLoad: 'Konnte Auto-Import-Einstellungen nicht laden.',
        runNow: 'Jetzt ausführen',
        running: 'Importiere…',
        runDone: 'Auto-Import ausgeführt.',
        errorRun: 'Auto-Import fehlgeschlagen.',
        lastRun: 'Letzter Lauf: {{at}}',
        lastRunNever: 'Noch nie ausgeführt.',
        resultConverted: 'Übernommen: {{count}}',
        resultSkipped: 'Übersprungen: {{count}}',
        resultErrors: 'Fehler: {{count}}',
        missingDefaultsWarning: 'Folgende Kalender sind zum Import aktiviert, aber es fehlen Behandler, Behandlung oder Raum: {{calendars}}. Diese werden nicht importiert, bis die Standardwerte vollständig sind.',
        globallyDisabledHint: 'Auto-Import ist global deaktiviert — ein Lauf übernimmt keine Termine.',
        notReadyHint: 'Service-Account nicht initialisiert — Auto-Import nicht möglich.',
      },
      // --- Event-Modal ---
      errorLoadMasters: 'Konnte Stammdaten nicht laden.',
      errorTitleEmpty: 'Titel darf nicht leer sein.',
      eventSaved: 'Event gespeichert.',
      confirmCancelEvent: 'Event in Google Calendar stornieren? (Event bleibt sichtbar, wird durchgestrichen.)',
      eventCancelled: 'Event storniert.',
      errorCancel: 'Stornierung fehlgeschlagen.',
      eventConfirmed: 'Event bestätigt.',
      errorAction: 'Aktion fehlgeschlagen.',
      confirmDeleteEvent: 'Event aus Google Calendar UNWIDERRUFLICH löschen? Das kann nicht rückgängig gemacht werden.',
      eventDeleted: 'Event gelöscht.',
      errorDelete: 'Löschen fehlgeschlagen.',
      errorConvertSelect: 'Bitte Behandler, Behandlung und Raum auswählen.',
      convertedToAppointment: 'Als App-Termin übernommen.',
      errorConvert: 'Konvertierung fehlgeschlagen.',
      eventEyebrow: 'Google-Calendar-Event',
      untitled: '(ohne Titel)',
      cancelledBadge: 'storniert',
      linkedBadge: 'verknüpft mit App-Termin #{{id}}',
      readOnlyBadge: 'Nur Anzeige — {{calendar}}',
      titleField: 'Titel',
      allDayHint: 'Ganztägiges Event — Zeiten können hier nicht editiert werden. Öffne es in Google Calendar.',
      start: 'Start',
      end: 'Ende',
      locationField: 'Ort',
      createdBy: 'Erstellt von: {{email}}',
      organizer: 'Organisator: {{email}}',
      openInGoogle: '↗ In Google Calendar öffnen',
      convertTitle: 'In App-Termin konvertieren',
      convertHint: 'Verknüpft das Google-Event mit einem neuen App-Termin. Patient/Behandler/Behandlung müssen einmal zugeordnet werden.',
      convertHintExtra: 'Übernimmt den Termin aus dem Zusatzkalender als App-Termin: Es wird eine Kopie im Hauptkalender angelegt, das Original im Zusatzkalender bleibt unverändert. Danach kannst du den Termin normal planen, bestätigen usw.',
      providerPlaceholder: 'Behandler…',
      procedurePlaceholder: 'Behandlung…',
      operatoryPlaceholder: 'Raum…',
      patientPlaceholder: 'Patient',
      emailOptionalPlaceholder: 'E-Mail (optional)',
      phoneOptionalPlaceholder: 'Telefon (optional)',
      converting: 'Konvertiere…',
      convert: 'Konvertieren',
      deleting: 'Lösche…',
      deleteEvent: '🗑 Löschen',
      toAppointment: '→ In App-Termin',
      confirming: 'Bestätige…',
      reactivate: 'Wieder aktivieren',
      cancelling: 'Storniere…',
      cancelEvent: 'Stornieren',
    },
  },
  es: {
    googleCalendar: {
      // --- Ajustes ---
      title: 'Google Calendar',
      description: 'Controla con qué cuenta de servicio y en qué calendario se escriben las citas.',
      loading: 'Cargando ajustes de Google Calendar…',
      saving: 'Guardando…',
      testing: 'Probando…',
      testConnection: 'Probar conexión',
      remove: 'Quitar',
      connected: 'Conectado',
      notConnected: 'No conectado',
      sourceLabel: 'Origen: {{source}}',
      sourcePersisted: 'Persistido (guardado desde la interfaz)',
      sourceEnv: 'Desde variable de entorno',
      sourceDefault: 'Predeterminado (calendario de la cuenta de servicio)',
      activeServiceAccount: 'Cuenta de servicio activa',
      unknown: '(desconocida)',
      projectLabel: 'Proyecto:',
      clientIdLabel: 'ID de cliente:',
      replaceServiceAccount: 'Sustituir la cuenta de servicio',
      replaceServiceAccountHint:
        'Suba el archivo de clave JSON desde Google Cloud Console (IAM & Admin → Cuentas de servicio → Claves → «Add key» → JSON). El archivo anterior se respalda automáticamente como <code>service-account.json.bak</code>; si la autenticación falla, se revierte.',
      uploadingAuthenticating: 'Subiendo y autenticando…',
      accessWaysHint:
        'Dos formas de que la cuenta pueda acceder a un calendario: (1) compartir el calendario directamente con la dirección de arriba con permiso <strong>«Realizar cambios en los eventos»</strong>, o (2) configurar la <strong>delegación a nivel de dominio</strong> e indicar en el campo <em>«Suplantar como»</em> de abajo el usuario de Workspace en cuyo nombre actuará la cuenta.',
      newMainCalendar: 'Nuevo calendario principal',
      newMainCalendarSub: '(predeterminado para nuevas citas)',
      activeBadge: 'ACTIVO',
      newMainCalendarHint:
        'Las nuevas citas se guardarán en este calendario a partir de ahora. Requisito: la cuenta de servicio <code>{{email}}</code> debe estar añadida en los ajustes de uso compartido con el permiso «Realizar cambios en los eventos». Déjelo vacío → el calendario anterior de abajo seguirá siendo el predeterminado.',
      saveFirstTitle: 'Guarde primero para poder probar',
      envFallbackAvailable: 'ℹ️ Alternativa de entorno disponible: <code>{{value}}</code>',
      envFallbackAvailableReset: 'ℹ️ Alternativa de entorno disponible: <code>{{value}}</code> (se aplica como predeterminado tras el restablecimiento).',
      legacyCalendar: 'Calendario anterior',
      legacyCalendarSub: '(citas existentes, predeterminado solo bajo petición)',
      legacyCalendarHint:
        'Las citas existentes permanecen en este calendario. Las nuevas citas solo se guardan aquí si se elige «Calendario anterior» al crearlas, o si arriba no hay ningún calendario principal configurado. ⚠️ Los cambios aquí pueden romper las sincronizaciones de citas existentes.',
      extraCalendars: 'Calendarios adicionales',
      extraCalendarsSub: '(solo lectura, nunca destino de escritura)',
      extraCalendarsHint:
        'Estos calendarios se muestran adicionalmente en el calendario del administrador — nunca se crean ni se modifican citas en ellos. Requisito: el propietario del calendario debe compartirlo con <code>{{email}}</code> (Google Calendar → Configuración → «Compartir con determinadas personas», el permiso «Ver todos los detalles de los eventos» es suficiente).',
      extraAdd: 'Añadir',
      extraSaved: 'Calendarios adicionales guardados.',
      extraAlreadyAdded: 'Este calendario ya está añadido.',
      pseudonymizedSync: 'Sincronización seudonimizada',
      pseudonymizedSyncSub: '(sin datos de pacientes en Google)',
      pseudonymizedSyncHint:
        'Si está activa, los eventos de Google solo contienen el tipo de tratamiento y el número de cita: el nombre, los datos de contacto y las notas del paciente permanecen en el servidor. Se aplica a citas creadas o modificadas a partir de ahora; los eventos existentes conservan su contenido hasta el próximo cambio.',
      pseudonymizedOn: 'Activa – eventos sin datos de pacientes',
      pseudonymizedOff: 'Inactiva – eventos con nombre y contacto del paciente',
      pseudonymizedSaved: 'Configuración de sincronización seudonimizada guardada.',
      impersonateAs: 'Suplantar como',
      impersonateAsSub: '(opcional, delegación a nivel de dominio)',
      impersonatePlaceholder: 'déjelo vacío para el valor estándar — o p. ej. smile@seadentpalma.com',
      impersonateHint:
        'Si su organización de Google Workspace bloquea el uso compartido externo de calendarios, indique aquí el usuario de Workspace en cuyo nombre debe actuar la cuenta de servicio. Requisito: en <code>admin.google.com → Seguridad → Control de API → Delegación a nivel de dominio</code> el ID de cliente <code>{{clientId}}</code> debe estar autorizado con los ámbitos <code>auth/calendar</code> y <code>auth/calendar.events</code>.',
      impersonateActive: '✓ Suplantación activa: <code>{{value}}</code>',
      resetNotNeededTitle: 'Actualmente no persistido — no es necesario restablecer',
      resetTitle: 'Volver a entorno/predeterminado',
      resetting: 'Restableciendo…',
      resetToDefault: 'Restablecer al predeterminado',
      serviceNotInitTitle: 'Servicio no inicializado',
      saveLegacyTitle: 'Guarda solo el calendario anterior + suplantación. El nuevo calendario principal tiene su propio botón de guardar arriba.',
      saveLegacy: 'Guardar el anterior',
      // Toasts / Errores de ajustes
      errorLoad: 'No se pudieron cargar los ajustes de Google Calendar.',
      errorCalendarIdEmpty: 'El ID de calendario no puede estar vacío.',
      settingsSaved: 'Ajustes guardados.',
      errorSave: 'Error al guardar.',
      accessOk: '✅ Acceso correcto — "{{summary}}" ({{timeZone}})',
      errorTest: 'Error en la prueba.',
      secondarySaved: '✅ Nuevo calendario principal guardado: {{id}}',
      secondaryRemoved: 'Nuevo calendario principal eliminado.',
      errorNotJson: 'Seleccione un archivo .json (clave de la cuenta de servicio de Google).',
      errorFileTooLarge: 'Archivo demasiado grande: los JSON de cuentas de servicio suelen ocupar menos de 5 KB.',
      confirmReplaceSA:
        '¿Sustituir la cuenta de servicio por "{{name}}"?\n\nEl archivo actual se respaldará como service-account.json.bak. Si la autenticación con la nueva cuenta falla, se revierte automáticamente.',
      saActive: '✅ Cuenta de servicio activa: {{email}}',
      errorUpload: 'Error al subir.',
      confirmReset: '¿Eliminar el ID de calendario persistido y volver al predeterminado (entorno/cuenta de servicio)?',
      resetDone: 'Restablecido al predeterminado.',
      errorReset: 'Error al restablecer.',
      // --- Estado de sincronización (comprobación automática por calendario) ---
      syncStatusTitle: 'Estado de sincronización',
      syncStatusSub: '(comprobado automáticamente al abrir)',
      syncRecheck: 'Volver a comprobar',
      syncRechecking: 'Comprobando…',
      syncChecking: 'Comprobando…',
      syncOk: 'Sincronización OK',
      syncOkRead: 'Lectura OK',
      syncOkWrite: 'Lectura + escritura OK',
      syncFailed: 'Error',
      syncRoleMain: 'Calendario principal (escritura)',
      syncRoleLegacy: 'Calendario anterior',
      syncRoleExtra: 'Calendario adicional (solo lectura)',
      syncNoCalendars: 'No hay calendarios configurados.',
      syncServiceDown: 'Servicio no inicializado — sincronización no posible. Revise la cuenta de servicio.',
      syncDetailOk: '«{{summary}}» · {{timeZone}}',
      // --- Importación automática de calendarios integrados ---
      autoImport: {
        title: 'Importación automática',
        subtitle: 'Convierte automáticamente los eventos nuevos de los calendarios integrados en citas de la aplicación.',
        enable: 'Activar la importación automática',
        windowBack: 'Días hacia atrás',
        windowAhead: 'Días hacia adelante',
        perCalendarHint: 'Defina por calendario si se importa y con qué valores predeterminados se crean las nuevas citas. El doctor, el tratamiento y la sala son obligatorios para poder importar un calendario.',
        mainCalendarLabel: 'Calendario principal',
        defaultProvider: 'Doctor',
        defaultProcedure: 'Tratamiento',
        defaultOperatory: 'Sala',
        providerPlaceholder: 'Elegir doctor…',
        procedurePlaceholder: 'Elegir tratamiento…',
        operatoryPlaceholder: 'Elegir sala…',
        enableCalendar: 'Importar',
        save: 'Guardar la importación automática',
        saving: 'Guardando…',
        saved: 'Ajustes de importación automática guardados.',
        errorSave: 'Error al guardar los ajustes de importación automática.',
        errorLoad: 'No se pudieron cargar los ajustes de importación automática.',
        runNow: 'Ejecutar ahora',
        running: 'Importando…',
        runDone: 'Importación automática ejecutada.',
        errorRun: 'Error en la importación automática.',
        lastRun: 'Última ejecución: {{at}}',
        lastRunNever: 'Nunca ejecutada.',
        resultConverted: 'Convertidas: {{count}}',
        resultSkipped: 'Omitidas: {{count}}',
        resultErrors: 'Errores: {{count}}',
        missingDefaultsWarning: 'Los siguientes calendarios están activados para importar, pero les faltan el doctor, el tratamiento o la sala: {{calendars}}. No se importarán hasta que los valores predeterminados estén completos.',
        globallyDisabledHint: 'La importación automática está desactivada globalmente — una ejecución no convertirá ninguna cita.',
        notReadyHint: 'Cuenta de servicio no inicializada — la importación automática no es posible.',
      },
      // --- Modal de eventos ---
      errorLoadMasters: 'No se pudieron cargar los datos maestros.',
      errorTitleEmpty: 'El título no puede estar vacío.',
      eventSaved: 'Evento guardado.',
      confirmCancelEvent: '¿Cancelar el evento en Google Calendar? (El evento permanece visible, tachado.)',
      eventCancelled: 'Evento cancelado.',
      errorCancel: 'Error al cancelar.',
      eventConfirmed: 'Evento confirmado.',
      errorAction: 'La acción ha fallado.',
      confirmDeleteEvent: '¿Eliminar el evento de Google Calendar de forma IRREVERSIBLE? Esto no se puede deshacer.',
      eventDeleted: 'Evento eliminado.',
      errorDelete: 'Error al eliminar.',
      errorConvertSelect: 'Seleccione doctor, tratamiento y sala.',
      convertedToAppointment: 'Convertido en cita de la aplicación.',
      errorConvert: 'Error en la conversión.',
      eventEyebrow: 'Evento de Google Calendar',
      untitled: '(sin título)',
      cancelledBadge: 'cancelado',
      linkedBadge: 'vinculado con la cita #{{id}}',
      readOnlyBadge: 'Solo lectura — {{calendar}}',
      titleField: 'Título',
      allDayHint: 'Evento de todo el día: las horas no se pueden editar aquí. Ábralo en Google Calendar.',
      start: 'Inicio',
      end: 'Fin',
      locationField: 'Lugar',
      createdBy: 'Creado por: {{email}}',
      organizer: 'Organizador: {{email}}',
      openInGoogle: '↗ Abrir en Google Calendar',
      convertTitle: 'Convertir en cita de la aplicación',
      convertHint: 'Vincula el evento de Google con una nueva cita de la aplicación. Paciente/doctor/tratamiento deben asignarse una vez.',
      convertHintExtra: 'Convierte la cita del calendario adicional en una cita de la aplicación: se crea una copia en el calendario principal y el original del calendario adicional permanece sin cambios. Después podrá planificarla, confirmarla, etc. con normalidad.',
      providerPlaceholder: 'Doctor…',
      procedurePlaceholder: 'Tratamiento…',
      operatoryPlaceholder: 'Sala…',
      patientPlaceholder: 'Paciente',
      emailOptionalPlaceholder: 'Correo electrónico (opcional)',
      phoneOptionalPlaceholder: 'Teléfono (opcional)',
      converting: 'Convirtiendo…',
      convert: 'Convertir',
      deleting: 'Eliminando…',
      deleteEvent: '🗑 Eliminar',
      toAppointment: '→ A cita de la aplicación',
      confirming: 'Confirmando…',
      reactivate: 'Reactivar',
      cancelling: 'Cancelando…',
      cancelEvent: 'Cancelar',
    },
  },
};
